import type { BoxProps } from "@qws-org/ui-kit";
import { Box, Flex } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useEffect, useId, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import type { Action, MainSlider as MainSliderType, Pages } from "~/core";
import { Counter } from "~/features/main-slider/components/Counter";
import { DesktopNavigationButtons } from "~/features/main-slider/components/DesktopNavigationButtons";
import { DesktopNavigationColumn } from "~/features/main-slider/components/DesktopNavigationColumn";
import { MainSliderButtons } from "~/features/main-slider/components/MainSliderButtons";
import { MobileNavigationButtons } from "~/features/main-slider/components/MobileNavigationButtons";
import { PaginationProgressDot } from "~/features/main-slider/components/PaginationProgressDot";
import { TextContent } from "~/features/main-slider/components/TextContent";
import { CmsImage } from "~/shared/ui/CmsImage";
import type { ImageParams } from "~/shared/utils/directus/useDirectus";
import { getPictureData } from "~/shared/utils/get-picture-data";

export type MainSliderProps = {
  data: MainSliderType;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  getImageUrl: (imageId: string, params?: ImageParams) => string;
  isControlInactive: (control: Action) => boolean;
  /** True when this block is rendered first on the page. */
  isFirstOnPage?: boolean;
} & BoxProps;

const getSlideImageSizes = (
  slidesPerView?: number | null,
): string | undefined => {
  if (!slidesPerView || slidesPerView <= 1) {
    return "(min-width: 1920px) 1920px, 100vw";
  }

  const desktopWidth = Math.ceil(100 / slidesPerView);
  const maxDesktopWidth = Math.ceil(1920 / slidesPerView);

  return `(min-width: 1920px) ${maxDesktopWidth}px, (min-width: 1024px) ${desktopWidth}vw, 100vw`;
};

const firstPositiveNumber = (
  ...values: Array<number | null | undefined>
): number | undefined => {
  for (const value of values) {
    if (typeof value === "number" && value > 0) {
      return value;
    }
  }

  return undefined;
};

export const MainSlider: FC<MainSliderProps> = (props) => {
  const { data, isFirstOnPage, ...rest } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const [progressRunId, setProgressRunId] = useState(0);
  const loop = data?.loop ?? false;
  const autoplayDelay = data.autoplay_delay ?? 5000;
  const pagination = data.pagination ?? false;
  const slideImageSizes = getSlideImageSizes(data.slides_per_view);
  useEffect(() => {
    setProgressRunId((v) => v + 1);
  }, [activeIndex]);
  const swiperRef = useRef<SwiperType | null>(null);
  const sliderId = `main-slider-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const navigationEnabled = data?.navigation_enabled;

  if (!data?.slides || data.slides.length === 0) {
    return null;
  }

  const firstSlideBg = getPictureData(
    data.slides[0]?.main_slider_slides_id?.background,
  );
  const mobileMinHeight =
    firstPositiveNumber(
      firstSlideBg.mHeight,
      firstSlideBg.mdHeight,
      firstSlideBg.dHeight,
    ) ?? 720;
  const tabletMinHeight =
    firstPositiveNumber(
      firstSlideBg.mdHeight,
      firstSlideBg.mHeight,
      firstSlideBg.dHeight,
    ) ?? mobileMinHeight;
  const desktopMinHeight =
    firstPositiveNumber(
      firstSlideBg.dHeight,
      firstSlideBg.mdHeight,
      firstSlideBg.mHeight,
    ) ?? tabletMinHeight;
  const sliderMinHeightCss = `#${sliderId},#${sliderId} .swiper-wrapper,#${sliderId} .swiper-slide,#${sliderId} .swiper-slide>div{min-height:${mobileMinHeight}px}#${sliderId}:not(.swiper-initialized) .swiper-slide:not(:first-child){position:absolute;inset:0;visibility:hidden}#${sliderId}:not(.swiper-initialized) .swiper-slide:not(:first-child) picture{display:none}#${sliderId}:not(.swiper-initialized) .swiper-slide:first-child>div{width:100%;max-width:768px;height:${mobileMinHeight}px}@media(min-width:768px){#${sliderId},#${sliderId} .swiper-wrapper,#${sliderId} .swiper-slide,#${sliderId} .swiper-slide>div{min-height:${tabletMinHeight}px}#${sliderId}:not(.swiper-initialized) .swiper-slide:first-child>div{max-width:1024px;height:${tabletMinHeight}px}}@media(min-width:1024px){#${sliderId},#${sliderId} .swiper-wrapper,#${sliderId} .swiper-slide,#${sliderId} .swiper-slide>div{min-height:${desktopMinHeight}px}#${sliderId}:not(.swiper-initialized) .swiper-slide:first-child>div{max-width:1920px;height:${desktopMinHeight}px}}`;

  return (
    <>
      <Flex
        width="100%"
        maxWidth={1240}
        align={"center"}
        m={{ mx: "auto" }}
        position={{ type: "relative" }}
      >
        <Flex
          display="hidden"
          position={{ type: "absolute", zIndex: 10, top: 140 }}
          md={{ display: "flex", position: { left: 20 } }}
          d={{ position: { left: 124 } }}
          align="flex-end"
          gap={16}
        >
          <Counter
            currentIndex={activeIndex}
            total={data?.slides?.length ?? 0}
          />

          {navigationEnabled && <DesktopNavigationButtons />}
        </Flex>
      </Flex>
      <style>{sliderMinHeightCss}</style>
      <Swiper
        id={sliderId}
        className="w-full"
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
        }}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        speed={data.speed ?? 500}
        autoplay={
          data.autoplay_delay
            ? {
                delay: autoplayDelay,
                pauseOnMouseEnter: data.pause_on_mouse_enter ?? true,
                disableOnInteraction: false,
              }
            : false
        }
        loop={loop}
        navigation={
          navigationEnabled
            ? { nextEl: ".custom-next", prevEl: ".custom-prev" }
            : false
        }
        centeredSlides={data.centered_slides ?? false}
        slidesPerView={data.slides_per_view ?? 1}
        grabCursor={true}
        modules={[Autoplay, Navigation]}
      >
        {data.slides?.map((slide, slideIndex) => {
          const slideBg = getPictureData(
            slide?.main_slider_slides_id?.background,
          );
          const hasImage = Boolean(slideBg.id);
          const slidePriority = (isFirstOnPage ?? false) && slideIndex === 0;
          return (
            <SwiperSlide className="h-full" key={slide?.id}>
              <Flex
                position={
                  hasImage ? { type: "relative", zIndex: 0 } : undefined
                }
                width="100%"
                justify={"center"}
                maxWidth={768}
                height={slideBg.mHeight}
                bg={hasImage ? undefined : slideBg.mColor}
                {...rest}
                md={{
                  justify: "normal",
                  bg: hasImage ? undefined : slideBg.mdColor,
                  maxWidth: 1024,
                  height: slideBg.mdHeight,

                  ...rest.md,
                }}
                d={{
                  bg: hasImage ? undefined : slideBg.dColor,

                  justify: navigationEnabled ? "space-between" : "center",
                  gap: 40,
                  maxWidth: 1920,
                  height: slideBg.dHeight,
                  ...rest.d,
                }}
              >
                {hasImage && (
                  <CmsImage
                    id={slideBg.id}
                    tabletId={slideBg.mdId}
                    desktopId={slideBg.dId}
                    width={
                      slideBg.dWidth ??
                      slideBg.mdWidth ??
                      slideBg.mWidth ??
                      1920
                    }
                    height={
                      slideBg.dHeight ??
                      slideBg.mdHeight ??
                      slideBg.mHeight ??
                      720
                    }
                    alt={slideBg.altText}
                    priority={slidePriority}
                    sizes={slideImageSizes}
                    style={{ position: "absolute", inset: 0, zIndex: -10 }}
                    className="absolute inset-0 -z-10 [&_img]:w-full [&_img]:h-full [&_img]:object-cover"
                  />
                )}
                {navigationEnabled && (
                  <DesktopNavigationColumn direction="left" />
                )}
                <Flex justify={"center"}>
                  <Flex
                    flexDirection="column"
                    width={"100%"}
                    md={{ minWidth: 1024, m: { mt: 220 }, gap: 64 }}
                    gap={0}
                    m={{ mt: 0 }}
                    p={{ px: 16 }}
                  >
                    <Flex align="center" md={{ display: "hidden" }}>
                      {navigationEnabled && <MobileNavigationButtons />}
                    </Flex>

                    <TextContent
                      description={slide?.main_slider_slides_id?.description}
                      title={slide?.main_slider_slides_id?.title}
                      items={slide?.main_slider_slides_id?.items}
                    />

                    {slide?.main_slider_slides_id?.controls && (
                      <MainSliderButtons
                        controls={slide.main_slider_slides_id.controls}
                        {...rest}
                      />
                    )}
                  </Flex>
                </Flex>
                {navigationEnabled && (
                  <DesktopNavigationColumn direction="right" />
                )}
              </Flex>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {pagination && (
        <Box position={{ type: "relative" }}>
          <Flex
            position={{ type: "absolute", zIndex: 10, top: -28 }}
            width={"100%"}
            height={"100%"}
            justify={"center"}
            display={"hidden"}
            md={{ display: "flex" }}
          >
            <Flex gap={4}>
              {data?.slides?.map((_, i) => {
                const state =
                  i < activeIndex
                    ? "fulfilled"
                    : i === activeIndex
                      ? "current"
                      : "future";

                return (
                  <PaginationProgressDot
                    key={`${i}-${activeIndex}`}
                    duration={autoplayDelay}
                    state={state}
                    resetKey={state === "current" ? progressRunId : 0}
                  />
                );
              })}
            </Flex>
          </Flex>
        </Box>
      )}
    </>
  );
};
